import { Box } from '@chakra-ui/layout';
import { type ReactNode, useEffect, useRef } from 'react';

import { NoData } from '../plot-loader/plot-loader';
import styles from './table-chart.module.css';

type RenderTableContentArgs = {
  isEmpty: boolean;
  useSplitHeaderLayout: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  totalHeight: number;
  headerRows: ReactNode;
  bodyRows: ReactNode;
};

export const RenderTableContent = ({
  isEmpty,
  useSplitHeaderLayout,
  scrollRef,
  totalHeight,
  headerRows,
  bodyRows,
}: RenderTableContentArgs): ReactNode => {
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const headerInnerRef = useRef<HTMLDivElement>(null);

  // Split-header layout: header table lives outside the scroll container so
  // the scrollbar's vertical track only covers the data rows. On body scroll,
  // translate the header's inner wrapper to keep columns horizontally aligned.
  // Batch the transform write to the next animation frame so it lands in the
  // same paint cycle as the body's scroll commit (avoids a 1-frame lag where
  // body and header drift apart on fast scrolls). translate3d forces the
  // header onto its own compositor layer for GPU-accelerated movement.
  const headerScrollFrameRef = useRef<number | null>(null);

  const handleBodyScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft } = event.currentTarget;
    if (headerScrollFrameRef.current !== null) {
      cancelAnimationFrame(headerScrollFrameRef.current);
    }
    headerScrollFrameRef.current = requestAnimationFrame(() => {
      headerScrollFrameRef.current = null;
      if (headerInnerRef.current) {
        headerInnerRef.current.style.transform = `translate3d(-${scrollLeft}px, 0, 0)`;
      }
    });
  };

  // Split-layout-only: forward wheel events from the (overflow: hidden) header
  // into the body's scroll element so wheel-over-header scrolls the body. The
  // listener is non-passive so we can preventDefault and stop the page (or any
  // ancestor) from also scrolling. Also tears down any pending header-sync rAF
  // queued by handleBodyScroll on unmount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: don't check change of ref
  useEffect(() => {
    if (!useSplitHeaderLayout) return;
    const headerEl = headerContainerRef.current;
    if (!headerEl) return;

    const clamp = (v: number, max: number) => Math.max(0, Math.min(max, v));

    const handleWheel = (event: WheelEvent) => {
      const target = scrollRef.current;
      if (!target) return;

      const nextLeft = clamp(
        target.scrollLeft + event.deltaX,
        target.scrollWidth - target.clientWidth
      );
      const nextTop = clamp(
        target.scrollTop + event.deltaY,
        target.scrollHeight - target.clientHeight
      );

      // At a boundary in both axes — let the wheel bubble so the page can scroll.
      if (nextLeft === target.scrollLeft && nextTop === target.scrollTop) {
        return;
      }
      event.preventDefault();
      target.scrollLeft = nextLeft;
      target.scrollTop = nextTop;
    };

    headerEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      headerEl.removeEventListener('wheel', handleWheel);
      if (headerScrollFrameRef.current !== null) {
        cancelAnimationFrame(headerScrollFrameRef.current);
      }
    };
  }, [useSplitHeaderLayout]);

  if (isEmpty) {
    return <NoData />;
  }

  if (useSplitHeaderLayout) {
    // Split layout: header table is outside the scroll container so the body's
    // scrollbar track only covers the data rows. The body's onScroll syncs the
    // header's horizontal offset via transform. Note: column header → cell
    // associations are split across two <table> elements; a follow-up could
    // add ARIA grid roles for stronger screen-reader semantics.
    return (
      <>
        <Box
          ref={headerContainerRef}
          overflow="hidden"
          className={styles.headerScrollContainer}
        >
          <div ref={headerInnerRef} style={{ minWidth: 'fit-content' }}>
            <table className={styles.table}>
              <thead className={styles.headerSection}>{headerRows}</thead>
            </table>
          </div>
        </Box>
        <Box
          ref={scrollRef}
          flex={1}
          overflow="auto"
          onScroll={handleBodyScroll}
          className={styles.stableScrollbarGutter}
        >
          <div style={{ height: `${totalHeight}px` }}>
            <table className={styles.table}>
              <tbody>{bodyRows}</tbody>
            </table>
          </div>
        </Box>
      </>
    );
  }

  // autoFitColumns: columns size to content, so the two-table split would
  // misalign. Fall back to the legacy single-table layout with a sticky thead
  // inside the scroll container.
  return (
    <Box
      ref={scrollRef}
      flex={1}
      overflow="auto"
      className={styles.stableScrollbarGutter}
    >
      <div style={{ height: `${totalHeight}px` }}>
        <table className={styles.table}>
          <thead className={styles.stickyHeader}>{headerRows}</thead>
          <tbody>{bodyRows}</tbody>
        </table>
      </div>
    </Box>
  );
};
